// Tiny DOM helpers - keeps the UI code declarative without pulling in a framework.

type Attrs = Record<string, string | number | boolean | EventListener | undefined>;

/**
 * Create an element. `tag` may include classes via `.` and an id via `#`,
 * e.g. el("div.card.big#main"). Children can be nodes or strings.
 */
export function el(
  tag: string,
  attrs: Attrs = {},
  ...children: (Node | string | null | undefined)[]
): HTMLElement {
  const idMatch = tag.match(/#([\w-]+)/);
  const classes = [...tag.matchAll(/\.([\w-]+)/g)].map((m) => m[1]);
  const name = tag.replace(/[.#][\w-]+/g, "") || "div";

  const node = document.createElement(name);
  if (idMatch) node.id = idMatch[1];
  if (classes.length) node.classList.add(...classes);

  for (const [key, value] of Object.entries(attrs)) {
    if (value == null || value === false) continue;
    if (key.startsWith("on") && typeof value === "function") {
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (key === "class") {
      node.className += " " + value;
    } else if (key === "html") {
      node.innerHTML = String(value);
    } else {
      node.setAttribute(key, String(value));
    }
  }

  for (const child of children) {
    if (child == null) continue;
    node.append(child instanceof Node ? child : document.createTextNode(child));
  }
  return node;
}

export function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function qs<T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T | null {
  return root.querySelector<T>(sel);
}
